import { useState, useEffect } from 'react'
import { ChevronLeft, Save } from 'lucide-react'
import { fetchUsers, fetchUserDetail, updateUserQuota } from '../../services/admin'
import type { AdminUser, AdminUserDetail } from '../../services/admin'

export default function UsersPanel() {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedUser, setSelectedUser] = useState<AdminUserDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  // Quota editing state
  const [editingQuota, setEditingQuota] = useState(false)
  const [editPlan, setEditPlan] = useState('')
  const [editJobsPerMonth, setEditJobsPerMonth] = useState(0)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    loadUsers()
  }, [])

  async function loadUsers() {
    setLoading(true)
    try {
      const data = await fetchUsers()
      setUsers(data)
    } catch {
      // Error already handled by service
    } finally {
      setLoading(false)
    }
  }

  async function selectUser(id: string) {
    setDetailLoading(true)
    try {
      const detail = await fetchUserDetail(id)
      setSelectedUser(detail)
      setEditingQuota(false)
      if (detail.quota) {
        setEditPlan(detail.quota.plan)
        setEditJobsPerMonth(detail.quota.jobs_per_month)
      }
    } catch {
      // Handle error
    } finally {
      setDetailLoading(false)
    }
  }

  async function saveQuota() {
    if (!selectedUser) return
    setSaving(true)
    try {
      await updateUserQuota(selectedUser.user.id, {
        plan: editPlan,
        jobs_per_month: editJobsPerMonth,
      })
      setEditingQuota(false)
      // Refresh detail
      await selectUser(selectedUser.user.id)
      await loadUsers()
    } catch {
      // Handle error
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <p className="font-mono text-sm text-slate-500 animate-pulse">Loading users...</p>
  }

  // Detail view
  if (selectedUser) {
    if (detailLoading) {
      return <p className="font-mono text-sm text-slate-500 animate-pulse">Loading user...</p>
    }

    const { user, quota, jobs, recentMessages, recentUsage } = selectedUser

    return (
      <div className="space-y-6">
        {/* Back button */}
        <button
          onClick={() => setSelectedUser(null)}
          className="flex items-center gap-1.5 font-mono text-xs text-slate-400 hover:text-cyan-400 transition-colors"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Back to users
        </button>

        {/* User info */}
        <div className="glass rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-mono text-sm text-slate-200">
                {user.email?.includes('@') ? user.email : <span className="text-slate-500">No email (ID: {user.email?.substring(0, 8)}...)</span>}
              </h2>
              {user.name && <p className="font-mono text-xs text-slate-500">{user.name}</p>}
              <p className="font-mono text-[10px] text-slate-600 mt-1">
                ID: {user.id}
              </p>
            </div>
            <p className="font-mono text-[10px] text-slate-500">
              Joined {new Date(user.created_at).toLocaleDateString()}
            </p>
          </div>

          {/* Quota section */}
          <div className="border-t border-cyan-500/10 pt-3 mt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[10px] tracking-widest uppercase text-slate-500">Quota</span>
              {!editingQuota && (
                <button
                  onClick={() => setEditingQuota(true)}
                  className="font-mono text-[10px] text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  Edit
                </button>
              )}
            </div>

            {editingQuota ? (
              <div className="flex items-end gap-3">
                <div>
                  <label className="font-mono text-[10px] tracking-widest uppercase text-slate-500 block mb-1">Plan</label>
                  <select
                    value={editPlan}
                    onChange={e => setEditPlan(e.target.value)}
                    className="bg-white/5 border border-cyan-500/20 rounded-lg px-3 py-1.5 font-mono text-xs text-slate-200 focus:border-cyan-400 outline-none"
                  >
                    <option value="invited" className="bg-[#030712] text-slate-200">invited</option>
                    <option value="pro" className="bg-[#030712] text-slate-200">pro</option>
                    <option value="admin" className="bg-[#030712] text-slate-200">admin</option>
                  </select>
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-widest uppercase text-slate-500 block mb-1">Jobs/month</label>
                  <input
                    type="number"
                    value={editJobsPerMonth}
                    onChange={e => setEditJobsPerMonth(parseInt(e.target.value) || 0)}
                    className="w-24 bg-white/5 border border-cyan-500/20 rounded-lg px-3 py-1.5 font-mono text-xs text-slate-200 focus:border-cyan-400 outline-none"
                  />
                </div>
                <button
                  onClick={saveQuota}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-xs bg-cyan-500/15 border border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/25 transition-all disabled:opacity-50"
                >
                  <Save className="w-3 h-3" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setEditingQuota(false)}
                  className="px-3 py-1.5 rounded-lg font-mono text-xs text-slate-400 hover:text-slate-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex gap-6">
                <div>
                  <span className="font-mono text-xs text-slate-400">Plan: </span>
                  <span className="font-mono text-xs text-slate-200">{quota?.plan ?? 'none'}</span>
                </div>
                <div>
                  <span className="font-mono text-xs text-slate-400">Jobs/month: </span>
                  <span className="font-mono text-xs text-slate-200">{quota?.jobs_per_month ?? 0}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Jobs */}
        <div>
          <h3 className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-3">
            Jobs ({jobs.length})
          </h3>
          {jobs.length === 0 ? (
            <p className="font-mono text-xs text-slate-600">No jobs</p>
          ) : (
            <div className="glass rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-cyan-500/10">
                    <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Label</th>
                    <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Material</th>
                    <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Created</th>
                    <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map(job => (
                    <tr key={job.id} className="border-b border-white/[0.06] last:border-0">
                      <td className="px-3 py-2 font-mono text-xs text-slate-200">{job.label}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-400">{job.material}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">{new Date(job.created_at).toLocaleDateString()}</td>
                      <td className="px-3 py-2">
                        <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full ${job.closed_at ? 'text-slate-500 bg-white/5' : 'text-emerald-400 bg-emerald-400/10'}`}>
                          {job.closed_at ? 'closed' : 'open'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recent Messages */}
        <div>
          <h3 className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-3">
            Recent Messages ({recentMessages.length})
          </h3>
          {recentMessages.length === 0 ? (
            <p className="font-mono text-xs text-slate-600">No messages</p>
          ) : (
            <div className="space-y-2">
              {recentMessages.slice(0, 20).map(msg => (
                <div key={msg.id} className="glass rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full ${msg.role === 'user' ? 'text-cyan-400 bg-cyan-400/10' : 'text-slate-400 bg-white/5'}`}>
                      {msg.role}
                    </span>
                    <span className="font-mono text-[10px] text-slate-600">
                      {new Date(msg.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="font-mono text-xs text-slate-300 whitespace-pre-wrap line-clamp-3">
                    {msg.content}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Usage */}
        <div>
          <h3 className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-3">
            Recent API Usage ({recentUsage.length})
          </h3>
          {recentUsage.length === 0 ? (
            <p className="font-mono text-xs text-slate-600">No usage</p>
          ) : (
            <div className="glass rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-cyan-500/10">
                    <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Model</th>
                    <th className="text-right px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Tokens</th>
                    <th className="text-right px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Cost</th>
                    <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentUsage.map((row, i) => (
                    <tr key={i} className="border-b border-white/[0.06] last:border-0">
                      <td className="px-3 py-2 font-mono text-xs text-slate-200">{row.model}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-400 text-right tabular-nums">{row.total_tokens.toLocaleString()}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-400 text-right tabular-nums">${parseFloat(row.cost_usd).toFixed(4)}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">{new Date(row.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    )
  }

  // Users list view
  return (
    <div>
      <p className="font-mono text-xs text-slate-500 mb-4">{users.length} users</p>
      <div className="glass rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-cyan-500/10">
              <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Email</th>
              <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Plan</th>
              <th className="text-right px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Jobs</th>
              <th className="text-left px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-slate-500">Joined</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr
                key={user.id}
                onClick={() => selectUser(user.id)}
                className="border-b border-white/[0.06] last:border-0 cursor-pointer hover:bg-white/[0.03] transition-colors"
              >
                <td className="px-3 py-2.5">
                  <div className="font-mono text-xs text-slate-200">
                    {user.email?.includes('@') ? user.email : <span className="text-slate-500">{user.email?.substring(0, 8)}...</span>}
                  </div>
                  {user.name && <div className="font-mono text-[10px] text-slate-500">{user.name}</div>}
                </td>
                <td className="px-3 py-2.5">
                  <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full ${
                    user.plan === 'pro' ? 'text-cyan-400 bg-cyan-400/10' :
                    user.plan === 'admin' ? 'text-amber-400 bg-amber-400/10' :
                    'text-slate-400 bg-white/5'
                  }`}>
                    {user.plan}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right">
                  <span className="font-mono text-xs text-slate-400 tabular-nums">
                    {user.job_count}/{user.jobs_per_month}
                  </span>
                </td>
                <td className="px-3 py-2.5 font-mono text-xs text-slate-500">
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
